import { motionOk } from "./kit";

/**
 * Full-screen colour flash (Phase 2 shared hook, PHASE2_BRIEF "Screen flash"): one pooled fixed `<div>` whose
 * opacity rises to `peak` in the first 20 % of `ms` and falls away — opacity only, one composited layer while it
 * runs. vfx calls it on the fortress strike, units on a boss's fall, the chest reveal on its burst.
 *
 * Safety (photosensitivity, and plain comfort on a phone held close at night): the peak is capped at `MaxPeak`, a
 * flash can't be shorter than `MinMs`, and flashes closer together than `MinGapMs` are merged into the running one
 * rather than strobing. Reduced effects / reduced motion: no flash at all.
 */
export const FLASH_LIMITS = { MaxPeak: 0.85, MinMs: 80, MaxMs: 1200, MinGapMs: 250 } as const;

/** The flash actually played for a request, or null when it should merge into the one still running. */
export function flashParams(peak: number, ms: number, sinceLastMs: number): { peak: number; ms: number } | null {
  if (sinceLastMs < FLASH_LIMITS.MinGapMs) return null;
  const p = Math.max(0, Math.min(FLASH_LIMITS.MaxPeak, Number.isFinite(peak) ? peak : 0));
  const d = Math.max(FLASH_LIMITS.MinMs, Math.min(FLASH_LIMITS.MaxMs, Number.isFinite(ms) ? ms : FLASH_LIMITS.MinMs));
  return p > 0 ? { peak: p, ms: d } : null;
}

let el: HTMLDivElement | null = null;
let last = -Infinity;

export const ScreenFlash = {
  /** `color`: any CSS colour ("#fff", "rgb(200,170,255)"); `peak`: 0..1 opacity; `ms`: total length. */
  flash(color = "#ffffff", peak = 0.6, ms = 160): void {
    if (typeof document === "undefined" || !motionOk()) return;
    const now = performance.now();
    const p = flashParams(peak, ms, now - last);
    if (!p) return;
    last = now;
    if (!el) {
      el = document.createElement("div");
      el.className = "screenflash";
      document.body.append(el);
    }
    el.style.background = color;
    if (typeof el.animate !== "function") return;
    el.getAnimations().forEach((a) => a.cancel());
    el.animate([{ opacity: 0 }, { opacity: p.peak, offset: 0.2 }, { opacity: 0 }], { duration: p.ms, easing: "ease-out" });
  },
};
